
import { Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { SchemeScope } from '@/components/mixins/schemescope';
import { Online } from '@/components/mixins/online';
import SearchField from '@/components/search/SearchField.vue';
import { LessonPlan } from '@/models/lessonPlan';
import { EventBus } from '@/services/event-bus';
import SchemeList from '@/components/coach/scheme/SchemeList.vue';
import AddLessonPlanForm from './AddLessonPlanForm.vue';
import _isEmpty from 'lodash/isEmpty';

@Component({
  components: {
    SearchField,
    SchemeList,
    AddLessonPlanForm,
  },
})
export default class PlanningList extends mixins(SchemeScope, Online) {
  private saving = false;
  private loading = false;
  private addingGroup = false;
  private newGroupName = '';
  private syncing = false;

  async created() {
    this.loading = true;
    await this.loadData();
    this.loading = false;
  }

  @Watch('$route')
  async loadData(force = false) {
    await this.initializeSchemeStore(false, false); // we need to prefetch the schemes to render skill titles
    if (this.schemeSlug) {
      this.loading = true;
      this.$store.commit('planning/updateSchemeSlug', this.schemeSlug);
      this.$store.commit('lessonPlanGroups/updateSchemeSlug', this.schemeSlug);
      this.$store.dispatch('nav/updatePlanningBreadcrumbs', { schemeSlug: this.schemeSlug });

      await this.$store.dispatch('planning/fetchItems', force);
      await this.$store.dispatch('lessonPlanGroups/fetchItems');

      // If this is the first page they view after login, then need to wait before fetching items
      EventBus.$on('user-initialized', async () => {
        await this.$store.dispatch('planning/fetchItems', force);
        await this.$store.dispatch('lessonPlanGroups/fetchItems');
      });

      EventBus.$on('member-sync-end', async () => {
        await this.$store.dispatch('nav/updatePlanningBreadcrumbs', { schemeSlug: this.schemeSlug });
      });

      this.loading = false;
    }
  }

  async sync() {
    this.syncing = true;
    await this.loadData(true);
    this.syncing = false;
  }

  // WITHOUT permission to Add/Edit Session Plans return only plans that apply to classes I have today.
  get items() {
    const sessionPlans = this.$store.state.planning.items as Array<LessonPlan>;
    if (this.canAddOrEditSession) {
      return sessionPlans;
    }
    const sessionPlanUuids = this.$store.getters['groups/sessionPlanUuids'];
    return sessionPlans.filter((plan: LessonPlan) => sessionPlanUuids.includes(plan.uuid));
  }

  get groupItems() {
    return this.$store.state.lessonPlanGroups.items;
  }

  get tab() {
    return this.$store.state.planning.tab;
  }

  set tab(tab: number) {
    this.$store.commit('planning/updateTab', tab);
  }

  get syncButtonText() {
    return this.syncing ? this.$t('planning.syncing') : this.$t('planning.sync');
  }

  get showLessonPlanGroups() {
    return (
      this.sessionPlanGroupsEnabled &&
      (this.$store.getters['auth/isFeatureEnabled']('CAN_ADD_SESSION_PLAN_GROUP') ||
        this.$store.getters['auth/isFeatureEnabled']('CAN_EDIT_SESSION_PLAN_GROUP'))
    );
  }

  isSharedOwner(item: LessonPlan) {
    return item.owner && item.owner.uuid === this.$store.getters['auth/loggedInUserId'] && item.shares.length;
  }

  async createGroup() {
    try {
      this.saving = true;
      await this.$store.dispatch('lessonPlanGroups/createItem', {
        title: this.newGroupName,
        schemeSlug: this.schemeSlug,
      });
      this.$alert('success', this.$t('planning.group_created') as string);
      this.newGroupName = '';
      this.addingGroup = false;
    } catch (error) {
      this.$alert('error', error as string);
    } finally {
      this.saving = false;
    }
  }

  async showAllSessions() {
    this.$store.commit('planning/updateItemsPerPage', 10000);
    await this.$store.dispatch('planning/fetchItems');
  }

  async showAllGroups() {
    this.$store.commit('lessonPlanGroups/updateItemsPerPage', 10000);
    await this.$store.dispatch('lessonPlanGroups/fetchItems');
  }

  selectItem(uuid: string) {
    this.$router
      .push({ name: 'lesson-plan', params: { schemeSlug: this.schemeSlug, lessonPlanUuid: uuid } })
      .catch(() => {
        // ignore routing errors
      });
  }

  selectGroup(uuid: string) {
    this.$router
      .push({ name: 'lesson-plan-group', params: { schemeSlug: this.schemeSlug, lessonPlanGroupUuid: uuid } })
      .catch(() => {
        // ignore routing errors
      });
  }

  get nameRules() {
    return [
      (v: string) => !!v || this.$t('validation.required'),
      (v: string) => (v && v.length <= 50) || this.$tc('validation.too_many_characters', 50),
    ];
  }

  get canAddOrEditSession() {
    return (
      this.$store.getters['auth/isFeatureEnabled']('CAN_ADD_SESSION_PLAN') ||
      this.$store.getters['auth/isFeatureEnabled']('CAN_EDIT_SESSION_PLAN')
    );
  }

  get canAddGroup() {
    return this.$store.getters['auth/isFeatureEnabled']('CAN_ADD_SESSION_PLAN_GROUP');
  }

  get sessionPlanGroupsEnabled() {
    return this.$store.getters['auth/isFeatureEnabled']('ENABLE_SESSION_PLANNING');
  }
}
